import {Injectable} from '@angular/core';
import {
    CognitoPermissions,
    SMMFeatEnum,
    RoleGroup,
    getPermissionMap,
    Permission,
    getPermissionById
} from "../types/permission.types";
import {decodeJWT} from "aws-amplify/auth";
import { Observable, of, tap} from "rxjs";
import {HttpClient} from "@angular/common/http";
import {UrlService} from "./url.service";
import {UrlEnum} from "../types/url-enum";
import {User} from "../types/smm/smm-person.types";
import {TranslocoService} from "@ngneat/transloco";

@Injectable({ providedIn: 'root' })
export class PermissionsService {
    private securityMaxGroups :RoleGroup [];
    private availableFeatures :Permission [] = [];
    private allGroups :RoleGroup [];

    constructor (
        private _httpClient:HttpClient,
        private _urlService : UrlService,
        private _translocoService: TranslocoService
    ) {

    }

    public initFeaturesFromToken():Observable<boolean>{
        // initialize features from token
        const features:string[] = this.getAvailableFeatures();
        features.forEach(featureID => {
            const permission = getPermissionById(featureID);
            if (permission) {
                this.availableFeatures.push(permission);
            }
        });
        return of(true);
    }

    private getAvailableFeatures(){
        //extract from token
        let idTokenName: string = Object.keys(localStorage)
            .filter(x =>
                x.endsWith('idToken'))
            .pop();

        let accessToken:string = localStorage.getItem('accessToken');
        let idToken:string = localStorage.getItem(idTokenName);
        if(accessToken && idToken) {
            let decoded = {
                accessToken: decodeJWT(accessToken),
                idToken: decodeJWT(idToken),
            }
            return decoded.idToken.payload["custom:perm"].toString().split(",") ;
        }
        else {
            return [];
        }
    }

    private getUserGroups() : string[] {
        let idTokenName: string = Object.keys(localStorage)
            .filter(x =>
                x.endsWith('idToken'))
            .pop();

        let accessToken:string = localStorage.getItem('accessToken');
        let idToken:string = localStorage.getItem(idTokenName);
        if(accessToken && idToken) {
            let decoded = {
                accessToken: decodeJWT(accessToken),
                idToken: decodeJWT(idToken),
            }
            return decoded.idToken.payload["cognito:groups"].toString().split(",") ;
        }
        else {
            return [];
        }
    }

    public isPlatformOwner():boolean{
        return this.getUserGroups().includes(CognitoPermissions.PLATFORM_ADMIN);
    }

    public isTenantOwner():boolean{
        return this.getUserGroups().includes(CognitoPermissions.TENANT_OWNER);
    }

    public isTenantAdmin():boolean{
        return this.getUserGroups().includes(CognitoPermissions.TENANT_ADMIN);
    }

    public isMedic():boolean{
        return this.getUserGroups().includes(CognitoPermissions.DOCTOR);
    }

    public isMedicOnly():boolean{
        return this.getUserGroups().length == 1 && this.getUserGroups().includes(CognitoPermissions.DOCTOR);
    }

    public isFrontDesk():boolean{
        return this.getUserGroups().includes(CognitoPermissions.FRONT_DESK);
    }

    public isFrontDeskOnly():boolean{
        return this.getUserGroups().length == 1 && this.getUserGroups().includes(CognitoPermissions.FRONT_DESK);
    }

    public isTeamMember():boolean{
        return this.isTenantOwner() ||  this.isTenantAdmin() ||  this.isMedic() || this.isFrontDesk();
    }

    public isPatient():boolean{
        return this.getUserGroups().includes(CognitoPermissions.PATIENT);
    }

    public isVisitor():boolean{
        return this.getUserGroups().includes(CognitoPermissions.VISITOR);
    }

    public userHasAnyOfRoles(roles:string[], anyOfRoles:CognitoPermissions[]):boolean {
        const filteredMaxGroups =  this.allGroups?.filter(g=> {
            return anyOfRoles.findIndex(role => role == g.name) > -1 && roles.includes(g.id)
        });
        return filteredMaxGroups?.length > 0;
    }

    public can(feat:SMMFeatEnum) {
        const debug = false;
        const isAble:boolean =  this.availableFeatures.findIndex(permission => permission.name == feat) > -1;
        if(debug){
            console.log("Can acces: ", feat, isAble);
        }
        return isAble;
    }

    public canByID(id:string) {
        const debug = false;
        const isAble:boolean =  this.availableFeatures.findIndex(permission => permission.id === id) > -1;
        if(debug){
             console.log("Can acces: ", id, isAble);
        }
        return isAble;
    }

    public getSecurityMaxGroups(): Observable<RoleGroup[]> {
        if(this.securityMaxGroups){
            return of(this.securityMaxGroups);
        }
        else{
            return this._httpClient.get<RoleGroup[]>(this._urlService.getUrl(UrlEnum.private_roles)).pipe(
                tap(groups=>{
                    this.securityMaxGroups = groups;
                })
            );
        }
    }

    public getAllGroups(): Observable<RoleGroup[]> {
        if(this.isPlatformOwner()){
            //no need of groups for platform owner
            return of([])
        }
        if(this.allGroups){
            return of(this.allGroups);
        }
        else{
            return this._httpClient.get<RoleGroup[]>(this._urlService.getUrl(UrlEnum.private_all_roles)).pipe(
                tap(groups=>{
                    this.allGroups = groups;
                })
            );
        }
    }

    public getRoleGroup(roleGroupID:string):RoleGroup|undefined{
        if(this.allGroups){
            for(let group of this.allGroups) {
                if(group.id == roleGroupID){
                    return group;
                }
            }
        }
        return undefined;
    }

    public getUserRolesTranslated(user:User, ):string{
        return user?.userRole?.map((roleID) => {
            return this._translocoService.translate(this.getRoleGroup(roleID)?.name);
        }).join(", ");
    }

    private wildcardMatch(text, pattern) {

        // Convert wildcard pattern to a
        // regular expression pattern
        const regexPattern = new RegExp(
            "^" +
            pattern
                .replace(/\?/g, ".")
                .replace(/\*/g, ".*") +
            "$"
        );

        // Test if the text matches the
        // regular expression pattern
        return regexPattern.test(text);
    }
}
